import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { LinkButton } from '../common/Button';
import SuccessLayout from '../SuccessLayout';

import styles from './SuccessScreen.module.scss';

import { useDownloadLinks } from '@/utils/appDownload';
import { getCurrentYear } from '@/utils/getCurrentYear';
import { env } from '@/utils/env';

interface SuccessScreenProps {
  /** Implies a new user, w/o an app */
  withDownload?: boolean;
  /** Implies a user who came from app */
  appLink?: string;
  loading?: boolean;
}

const SuccessScreen = ({ withDownload = false, appLink = '', loading }: SuccessScreenProps) => {
  const { currentPlatformLink, hasFetchedPersonalizedLinks } = useDownloadLinks();

  useEffect(() => {
    if (hasFetchedPersonalizedLinks && withDownload && !appLink) {
      window.location.href = currentPlatformLink;
    }
  }, [hasFetchedPersonalizedLinks]);

  return (
    <SuccessLayout>
      <main className={styles.main}>
        <h1 className={styles.title}>
          <FormattedMessage
            id="successScreen.title"
            defaultMessage="Welcome on board!"
            description="Title of the screen shown after successful purchase"
          />
        </h1>
        <span className={styles.subtitle}>
          <FormattedMessage
            id="successScreen.subtitle"
            defaultMessage="Your subscription is active!"
            description="Subtitle of the screen shown after successful purchase"
          />
        </span>

        {appLink ? (
          <LinkButton href={appLink} kind="white" className={styles.button} loading={loading}>
            <FormattedMessage
              id="successScreen.goToAppButton"
              defaultMessage="Go to app"
              description="Deeplink back to app after successful purchase"
            />
          </LinkButton>
        ) : (
          <LinkButton href={`${env.cabinetUrl}/profile`} kind="white" className={styles.button}>
            <FormattedMessage
              id="successScreen.goToAccountButton"
              defaultMessage="Go to my account"
              description="Link to user's cabinet"
            />
          </LinkButton>
        )}

        {appLink && (
          <span className={styles.downloadPara}>
            <FormattedMessage
              id="successScreen.downloadSectionForAppUser"
              values={{ lineBreak: <br />, downloadLink: (chunks) => <a href={currentPlatformLink}>{chunks}</a> }}
              description="Success screen's download section for existing user, implies that user has the app installed, but still."
              defaultMessage="Don’t have ClearVPN app installed?
                {lineBreak}
                <downloadLink>Download</downloadLink>"
            />
          </span>
        )}
        {withDownload && !appLink && (
          <span className={styles.downloadPara}>
            <FormattedMessage
              id="successScreen.downloadSection"
              values={{ lineBreak: <br />, downloadLink: (chunks) => <a href={currentPlatformLink}>{chunks}</a> }}
              description="Success screen's download section"
              defaultMessage="ClearVPN desktop app will start downloading now, to save your priceless time.
                {lineBreak}
                If your download didn’t start, <downloadLink>download manually</downloadLink>"
            />
          </span>
        )}
      </main>

      <footer className={styles.footer}>
        <div className={styles.footerLinks}>
          <a href="https://clearvpn.com/privacy-policy/">
            <FormattedMessage
              id="successScreen.footer.privacyPolicyLink"
              defaultMessage="Privacy policy"
              description="Link to privacy policy page"
            />
          </a>
          <a href="https://clearvpn.com/terms-of-service/">
            <FormattedMessage
              id="successScreen.footer.termsOfServiceLink"
              defaultMessage="Terms & Conditions"
              description="Link to terms of service page"
            />
          </a>
          <a href="https://support.clearvpn.com/hc/requests/new">
            <FormattedMessage
              id="successScreen.footer.supportLink"
              defaultMessage="Support"
              description="Link to support page"
            />
          </a>
        </div>

        <span className={styles.footerCopyright}>© {getCurrentYear()} ClearVPN</span>
      </footer>
    </SuccessLayout>
  );
};

export default SuccessScreen;
